import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/ui/sheet";
import { Form } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { Button } from "@/shared/ui/button";
import { Eye, EyeOff, Loader2, Lock } from "lucide-react";
import { Section, Field } from "./AppFormSections";
import { emptyAppForm } from "./appFormUtils";
import type { WeComApp, WeComAppRequest } from "@/entities/wecom-app";

type Props = {
  open: boolean;
  editing: WeComApp | null;
  pending: boolean;
  onSubmit: (req: WeComAppRequest) => void;
  onOpenChange: (open: boolean) => void;
};

export function WeComConfigForm({ open, editing, pending, onSubmit, onOpenChange }: Props) {
  const { t } = useTranslation("wecom");
  const [showSecrets, setShowSecrets] = useState(false);

  const schema = z.object({
    corpId: z.string().trim().min(1, t("appList.corpIdRequired")),
    agentId: z.number().int().nullable(),
    appSecret: z.string(),
    callbackToken: z.string(),
    encodingAesKey: z.string(),
    label: z.string(),
    enabled: z.boolean(),
    callbackDomain: z.string().trim().min(1, t("appList.callbackDomainRequired")),
    companyName: z.string().nullable(),
    advisorEnabled: z.boolean(),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: { ...emptyAppForm(), advisorEnabled: true } as unknown as Values,
  });

  // 打开 sheet 时按 editing 重置(编辑态密钥留空=保持)。
  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.reset({
        corpId: editing.corpId,
        agentId: editing.agentId ?? null,
        appSecret: "",
        callbackToken: "",
        encodingAesKey: "",
        label: editing.label ?? "",
        enabled: editing.enabled,
        callbackDomain: editing.callbackDomain ?? "",
        companyName: editing.companyName ?? null,
        advisorEnabled: editing.identityMode === "TENANT_CHAIN",
      });
    } else {
      form.reset({ ...emptyAppForm(), advisorEnabled: true } as unknown as Values);
    }
    setShowSecrets(false);
  }, [open, editing, form]);

  const submit = (v: Values) => {
    const req: WeComAppRequest = {
      corpId: v.corpId,
      agentId: v.agentId,
      appSecret: v.appSecret,
      callbackToken: v.callbackToken,
      encodingAesKey: v.encodingAesKey,
      label: v.label,
      enabled: v.enabled,
      module: "customer_service",
      callbackDomain: v.callbackDomain,
      companyName: v.companyName,
      identityMode: v.advisorEnabled ? "TENANT_CHAIN" : "MEMBER_CHANNEL",
    };
    onSubmit(req);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{editing ? t("appList.editConfig") : t("appList.startConfigure")}</SheetTitle>
          <p className="text-xs text-muted-foreground mt-1">{t("appList.unifiedSubtitle")}</p>
        </SheetHeader>
        <Form {...form}>
          <form className="space-y-6 mt-6" onSubmit={form.handleSubmit(submit)}>
            <Section
              title={t("appList.sectionCredentials")}
              hint={t("appList.sectionCredentialsHint")}
            >
              <Field label={t("appList.fieldLabel")} hint={t("appList.fieldLabelHint")}>
                <Input placeholder={t("appList.labelPlaceholder")} {...form.register("label")} />
              </Field>
              <Field label={t("appList.fieldCorpId")} required>
                <Input
                  aria-label={t("appList.fieldCorpId")}
                  placeholder={t("appList.corpIdPlaceholder")}
                  {...form.register("corpId")}
                />
                {form.formState.errors.corpId && (
                  <p className="text-2xs text-destructive">
                    {form.formState.errors.corpId.message}
                  </p>
                )}
              </Field>
              <Field label={t("appList.fieldAgentId")} hint={t("appList.agentIdHint")}>
                <Controller
                  control={form.control}
                  name="agentId"
                  render={({ field }) => (
                    // type="text" 而非 number:number 输入框聚焦时滚轮会自动增减数字(误改 agentId
                    // → 保存写库错值)。改文本 + inputMode 数字键盘 + 只保留数字,既防滚轮也去掉上下箭头。
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder={t("appList.agentIdPlaceholder")}
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        field.onChange(digits === "" ? null : Number(digits));
                      }}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </Field>
              <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2.5">
                <div>
                  <Label className="text-sm">{t("appList.enableLabel")}</Label>
                  <p className="text-2xs text-muted-foreground mt-0.5">
                    {t("appList.enableHint")}
                  </p>
                </div>
                <Controller
                  control={form.control}
                  name="enabled"
                  render={({ field }) => (
                    <Switch checked={field.value ?? true} onCheckedChange={field.onChange} />
                  )}
                />
              </div>
            </Section>

            <Section
              title={t("appList.sectionConnection")}
              icon={<Lock className="h-3 w-3" />}
              right={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 text-2xs"
                  onClick={() => setShowSecrets(!showSecrets)}
                >
                  {showSecrets ? (
                    <EyeOff className="h-3 w-3 mr-1" />
                  ) : (
                    <Eye className="h-3 w-3 mr-1" />
                  )}
                  {showSecrets ? t("appList.hideSecrets") : t("appList.showSecrets")}
                </Button>
              }
            >
              <Field
                label={t("appList.fieldCallbackDomain")}
                required
                hint={t("appList.callbackDomainHint")}
              >
                <Input
                  aria-label={t("appList.fieldCallbackDomain")}
                  placeholder={t("appList.callbackDomainPlaceholder")}
                  {...form.register("callbackDomain")}
                />
                {form.formState.errors.callbackDomain && (
                  <p className="text-2xs text-destructive">
                    {form.formState.errors.callbackDomain.message}
                  </p>
                )}
              </Field>
              <Field
                label={t("appList.fieldAppSecret")}
                required={!editing}
                hint={t("appList.appSecretHint")}
              >
                <Input
                  aria-label={t("appList.fieldAppSecret")}
                  type={showSecrets ? "text" : "password"}
                  placeholder={
                    editing ? t("appList.leaveBlankToKeep") : t("appList.appSecretPlaceholder")
                  }
                  {...form.register("appSecret")}
                />
              </Field>
              <Field label={t("appList.fieldCallbackToken")}>
                <Input
                  type={showSecrets ? "text" : "password"}
                  placeholder={
                    editing ? t("appList.leaveBlankToKeep") : t("appList.callbackTokenPlaceholder")
                  }
                  {...form.register("callbackToken")}
                />
              </Field>
              <Field label={t("appList.fieldEncodingAesKey")}>
                <Input
                  type={showSecrets ? "text" : "password"}
                  placeholder={
                    editing ? t("appList.leaveBlankToKeep") : t("appList.encodingAesKeyPlaceholder")
                  }
                  {...form.register("encodingAesKey")}
                />
              </Field>
            </Section>

            <Section title={t("appList.sectionCapability")}>
              <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
                <div>
                  <Label className="text-sm">{t("appList.capAdvisor")}</Label>
                  <p className="text-2xs text-muted-foreground mt-0.5">
                    {t("appList.capAdvisorDesc")}
                  </p>
                </div>
                <Controller
                  control={form.control}
                  name="advisorEnabled"
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      aria-label={t("appList.capAdvisor")}
                    />
                  )}
                />
              </div>
            </Section>

            <Section title={t("appList.sectionPersona")} hint={t("appList.sectionPersonaHint")}>
              <Field label={t("appList.fieldCompanyName")} hint={t("appList.companyNameHint")}>
                <Controller
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <Input
                      maxLength={64}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </Field>
            </Section>

            <Button type="submit" className="w-full" disabled={pending}>
              {pending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("appList.saving")}
                </>
              ) : (
                t("appList.confirmSave")
              )}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
